import { FC, useState } from "react";
import { useForm } from "@refinedev/antd";
import { useDelete } from "@refinedev/core";
import { Button, Form, Input, Modal, Space } from "antd";
import { CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { AppSimpleLink, AppViewCard } from "../../../../../components";
import { errorNotification, successNotification } from "../../../../../helpers";
import s from "../Clubs-Change-Email/Clubs-Change-Email.module.scss";

type ClubsChangeShopProps = {
  leagueId: number;
  teamId: number;
  shop?: string | null;
  onRefetchCard: () => void;
};

const SHOP_RULES = [
  { required: true, message: "Необходимо указать ссылку" },
  {
    pattern: /^https?:\/\/\S+\.\S+/,
    message: "Ссылка должна начинаться с https://",
  },
  { max: 1000, message: "Не более 1000 символов" },
];

/**
 * Интернет-магазин клуба: кнопка в блоке «Мерч» на странице матча ФНЛ ТВ.
 * Хранится как ссылка клуба типа SHOP — тот же API, что у соцсетей.
 */
export const ClubsChangeShop: FC<ClubsChangeShopProps> = ({
  teamId,
  leagueId,
  shop,
  onRefetchCard,
}) => {
  const [edit, setEdit] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const { mutate: deleteMutate } = useDelete();

  const { formProps, form } = useForm<{ typeLink: string; url: string }>({
    action: "create",
    resource: "socialLinks",
    meta: { teamId, leagueId },
    dataProviderName: "socialLinksDataProvider",
    redirect: false,
    ...successNotification({
      successData: {
        message: shop
          ? "Ссылка на магазин изменена"
          : "Ссылка на магазин добавлена",
        type: "success",
      },
      callback: () => {
        setEdit(false);
        onRefetchCard();
      },
    }),
    ...errorNotification({}),
  });

  const handleDelete = () =>
    deleteMutate({
      dataProviderName: "socialLinksDataProvider",
      resource: "socialLinks",
      id: "",
      meta: { teamId, leagueId, linkType: "SHOP" },
      ...successNotification({
        successData: { message: "Ссылка на магазин удалена", type: "success" },
        callback: () => {
          setConfirm(false);
          form.resetFields();
          onRefetchCard();
        },
      }),
      ...errorNotification({}),
    });

  if (shop && !edit) {
    return (
      <AppViewCard label={"Интернет-магазин:"}>
        <Space
          direction={"horizontal"}
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
          size={24}
        >
          <AppSimpleLink link={shop}>{shop}</AppSimpleLink>
          <div className={s.wrapContainer}>
            <EditOutlined
              style={{
                fontSize: 20,
                cursor: "pointer",
                color: "var(--accent-main)",
              }}
              onClick={() => setEdit(true)}
            />
            <DeleteOutlined
              style={{
                fontSize: 20,
                cursor: "pointer",
                color: "var(--danger)",
              }}
              onClick={() => setConfirm(true)}
            />
          </div>
        </Space>
        <Modal
          open={confirm}
          title={"Удалить ссылку на магазин?"}
          onOk={handleDelete}
          onCancel={() => setConfirm(false)}
          okText={"Удалить ссылку"}
          cancelText={"Отмена"}
        >
          Кнопка магазина пропадёт из блока «Мерч» на странице матча ФНЛ ТВ
        </Modal>
      </AppViewCard>
    );
  }

  return (
    <Form
      style={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
      }}
      {...formProps}
      initialValues={{ typeLink: "SHOP", url: shop ?? undefined }}
      layout={"vertical"}
    >
      <Form.Item name={"typeLink"} hidden />
      <Form.Item
        name={"url"}
        label={"Интернет-магазин:"}
        extra={"Кнопка клуба в блоке «Мерч» на странице матча ФНЛ ТВ"}
        style={{ width: "100%" }}
        rules={SHOP_RULES}
      >
        <Input placeholder={"https://shop.club.ru"} />
      </Form.Item>
      <Button className={s.button} type={"primary"} htmlType={"submit"}>
        {shop ? "Сохранить" : "Добавить магазин"}
      </Button>
      {shop && (
        <div className={s.iconContainer} style={{ marginTop: 30 }}>
          <CloseOutlined
            style={{
              fontSize: 20,
              cursor: "pointer",
              color: "var(--accent-main)",
            }}
            onClick={() => {
              setEdit(false);
              form.setFieldValue("url", shop);
            }}
          />
        </div>
      )}
    </Form>
  );
};
